import React from 'react';
import { useEffect, useRef, useState } from 'react';

interface CounterProps {
  value: number;
  duration?: number; // animation duration in ms
  formatter?: (val: number) => string; // optional number formatter
}

export const Counter = ({
  value,
  duration = 1000,
  formatter,
}: CounterProps) => {
  const [count, setCount] = useState(0);
  const startTime = useRef<number | null>(null);
  const startValue = useRef(0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    cancelAnimationFrame(frameRef.current!);

    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp;
      const progress = Math.min((timestamp - startTime.current) / duration, 1);

      // easeOutCubic for smoother deceleration
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const current =
        startValue.current + (value - startValue.current) * easedProgress;

      setCount(current);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        startValue.current = value; // ensure final value is accurate
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frameRef.current!);
  }, [value, duration]);

  return <>{formatter ? formatter(Math.round(count)) : Math.round(count)}</>;
};
